import { useCallback, useMemo } from "react";
import axios from "axios";
import { useSelector } from "react-redux";

import { i18n } from "../i18n/i18n";

function useApi() {
  const baseURL = process.env.REACT_APP_API_URL;

  const { token } = useSelector((store) => store.auth);

  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        validateStatus: (status) => [200, 400, 401].includes(status),
        headers: {
          authorization: token && `Bearer ${token}`,
          lang: i18n.currentLocale(),
        },
      }),
    [baseURL, token],
  );

  const ApiSignUp = useCallback(async (params) => await api.post("/auth/signup", params), [api]);
  const ApiSignIn = useCallback(async (params) => await api.post("/auth/signin", params), [api]);
  const ApiSignInWithProvider = useCallback(
    async (params) => await api.post("/auth/signin-with-provider", params),
    [api],
  );
  const ApiRecovery = useCallback(async (params) => await api.post("/auth/recovery", params), [api]);
  const ApiCreateNewPassword = useCallback(async (params) => await api.post("/auth/update-password", params), [api]);
  const ApiChangePassword = useCallback(async (params) => await api.put("/user/change-password", params), [api]);
  const ApiSignInTFA = useCallback(async (params) => await api.post("/auth/tfa", params), [api]);
  const ApiGenerateTFA = useCallback(async (params) => await api.post("/auth/generate-tfa", params), [api]);
  const ApiGenerateNewCode = useCallback(async (token) => await api.post("/auth/generate-new-code", { token }), [api]);

  const ApiGetUser = useCallback(async () => await api.get("/user"), [api]);
  const ApiUpdateUser = useCallback(async (params) => await api.put("/user", params), [api]);
  const ApiRequestNewEmail = useCallback(async () => await api.post("/user/request-new-email"), [api]);
  const ApiChangeEmail = useCallback(
    async (token, params) => await api.put(`/user/change-email/${token}`, params),
    [api],
  );

  const ApiAdminUpdateUser = useCallback(async (params) => await api.put("/admin/update-users", params), [api]);
  const ApiAdminGetUserByName = useCallback(async (name) => await api.get(`/user-by-name/${name}`), [api]);

  const ApiGetCredits = useCallback(async () => await api.get("/admin/credits"), [api]);
  const ApiGetCupons = useCallback(async () => await api.get("/admin/cupons"), [api]);

  const ApiGetLoyalMember = useCallback(async () => await api.get("/admin/loyal-member"), [api]);

  const ApiCheckout = useCallback(async (params) => await api.post("/checkout", params), [api]);

  const ApiGetLoyalMemberExpires = useCallback(async () => await api.get("/admin/loyal-member-expires"), [api]);
  const ApiGetLoyalMemberRewards = useCallback(
    async (loyalMemberCategory, serverId) =>
      await api.get("/admin/loyal-member-rewards", { params: { loyalMemberCategory, serverId } }),
    [api],
  );

  const ApiRewardsTake = useCallback(async (params) => await api.post("/rewards/take", params), [api]);

  const ApiCreateLogin = useCallback(async (params) => await api.post("/ddtank/create-login", params), [api]);
  const ApiGetServers = useCallback(async () => await api.get("/ddtank/servers"), [api]);
  const ApiGetCharacters = useCallback(async (areaId) => await api.get(`/ddtank/characters/${areaId}`), [api]);

  const ApiDailyDrawCanPlay = useCallback(async () => await api.get("/daily-draw/can-play"), [api]);
  const ApiDailyDrawResult = useCallback(async () => await api.post("/daily-draw/result"), [api]);
  const ApiGetLastWinners = useCallback(async () => await api.get("/daily-draw/last-winners"), [api]);

  const ApiSearchFaqs = useCallback(async (query) => await api.get(`/admin/faq/search/${query}`), [api]);
  const ApiGetFaqCommons = useCallback(async () => await api.get("/admin/faq/common"), [api]);
  const ApiCreateFaqVote = useCallback(async (params) => await api.post("/admin/faq/vote", params), [api]);
  const ApiGetFaqArticles = useCallback(async () => await api.get("/admin/faq"), [api]);
  const ApiCreateFaqArticle = useCallback(async (params) => await api.post("/admin/faq", params), [api]);
  const ApiUpdateFaqArticle = useCallback(async (id, params) => await api.put(`/admin/faq/${id}`, params), [api]);
  const ApiDeleteFaqArticle = useCallback(async (id) => await api.delete(`/admin/faq/${id}`), [api]);
  const ApiGetFaqFrequent = useCallback(async () => await api.get("/admin/faq/frequent"), [api]);
  const ApiGetFaqTheme = useCallback(async (params) => await api.post("/admin/faq/theme", params), [api]);
  const ApiGetFaqArticle = useCallback(async (id) => await api.get(`/admin/faq/${id}`), [api]);

  const ApiCreateTicket = useCallback(async (params) => await api.post("/ticket", params), [api]);
  const ApiGetTickets = useCallback(async () => await api.get("/tickets"), [api]);
  const ApiGetTicketInfo = useCallback(async (id) => await api.get(`/ticket-info/${id}`), [api]);
  const ApiGetTicketById = useCallback(async (id) => await api.get(`/tickets/${id}`), [api]);
  const ApiChangeTicketStatus = useCallback(async (id, params) => await api.put(`/ticket/${id}`, params), [api]);
  const ApiGetTicketsByUser = useCallback(async () => await api.get("/tickets/by-user"), [api]);
  const ApiSendTicketResponse = useCallback(
    async (id, params) => await api.post(`/ticket/${id}/response`, params),
    [api],
  );

  const ApiSendNotificationEmail = useCallback(async (params) => await api.post("/notification/email", params), [api]);
  const ApiSendNotificationPhone = useCallback(async (params) => await api.post("/notification/phone", params), [api]);

  return {
    api,
    ApiGenerateTFA,
    ApiSignUp,
    ApiSignIn,
    ApiSignInWithProvider,
    ApiRecovery,
    ApiCreateNewPassword,
    ApiChangePassword,
    ApiSignInTFA,
    ApiGetUser,
    ApiRequestNewEmail,
    ApiChangeEmail,
    ApiUpdateUser,
    ApiAdminUpdateUser,
    ApiAdminGetUserByName,
    ApiGetCredits,
    ApiGetCupons,
    ApiGetLoyalMember,
    ApiCheckout,
    ApiGetLoyalMemberExpires,
    ApiGetLoyalMemberRewards,
    ApiRewardsTake,
    ApiCreateLogin,
    ApiGetServers,
    ApiGetCharacters,
    ApiDailyDrawCanPlay,
    ApiDailyDrawResult,
    ApiGetLastWinners,
    ApiCreateFaqVote,
    ApiSearchFaqs,
    ApiGetFaqCommons,
    ApiGetFaqArticles,
    ApiCreateFaqArticle,
    ApiUpdateFaqArticle,
    ApiDeleteFaqArticle,
    ApiGetFaqFrequent,
    ApiGetFaqTheme,
    ApiCreateTicket,
    ApiGetTickets,
    ApiSendTicketResponse,
    ApiGetTicketsByUser,
    ApiGetTicketInfo,
    ApiGetTicketById,
    ApiSendNotificationEmail,
    ApiSendNotificationPhone,
    ApiGetFaqArticle,
    ApiChangeTicketStatus,
    ApiGenerateNewCode,
  };
}

export default useApi;
